
import Vue, { PropType } from 'vue';

/**
 * Vertical left-rail step list (Vuetify v-stepper adapter).
 *
 * Contract (consumers e.g. batch drawer):
 * - `value` is 0-based active step index; ignored for active styling when mode is generating/complete.
 * - `mode === 'wizard'`: active step = value; completed steps = index &lt; value; rail clicks only navigate
 *   backward (`canNavigateBack`: index &lt; value).
 * - `mode === 'generating' | 'complete'`: all steps show complete; none active (sentinel step value).
 */
export interface WindowStepItem {
	id: string
	label: string
	subtitle?: string
}

export default Vue.extend({
	name: 'WindowStepsPanel',
	props: {
		steps: { type: Array as PropType<WindowStepItem[]>, required: true },
		/** Active wizard step index (0-based); rail uses sentinel when mode is generating/complete */
		value: { type: Number, default: 0 },
		mode: { type: String as PropType<'wizard' | 'generating' | 'complete'>, default: 'wizard' },
		panelStyles: { type: [Object, String], default: () => ({}) },
	},
	computed: {
		/** Vuetify stepper uses 1-based steps; use sentinel when no step should be active. */
		vuetifyStepValue (): number {
			if (this.mode === 'wizard') return this.value + 1;
			return this.steps.length + 1;
		},
	},
	methods: {
		isActive (i: number) {
			return this.mode === 'wizard' && i === this.value;
		},
		isComplete (i: number) {
			if (this.mode === 'wizard') return i < this.value;
			if (this.mode === 'generating' || this.mode === 'complete') return true;
			return false;
		},
		canNavigateBack (i: number) {
			return this.mode === 'wizard' && i < this.value;
		},
		stepperColor (i: number) {
			if (this.isComplete(i) && !this.isActive(i)) return 'var(--primary-blue-2)';
			if (this.isActive(i)) return 'var(--grey-100)';
			return 'var(--grey-700)';
		},
		onStepClick (i: number) {
			if (!this.canNavigateBack(i)) return;
			this.$emit('input', i);
		},
	},
});
